import { Fragment, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import Icon from '../icons'
import { can } from '../api/permissions'
import LibraryFolderTree from './LibraryFolderTree'
import { MENU_MODULES } from '../api/menu'
import { PendingCount, useReviewNotifications } from '../components/ReviewNotifications'

export interface NavItem {
  to: string
  key: string
  label: string
  ico: string
  permissions: readonly string[]
  count?: ReactNode
  admin?: boolean
}

const NAV: readonly NavItem[] = MENU_MODULES.filter(item => !item.admin)
const NAV_ADMIN: readonly NavItem[] = MENU_MODULES.filter(item => item.admin)

/** 面包屑与页标题：与原型 TITLES 对齐（一级 / 二级）。 */
const TITLES: Record<string, [string, string]> = {
  library: ['售前知识库', '售前知识库 / 目录与资产'],
  'historical-materials': ['文件清理', '管理员 / 文件存储与清理状态'],
  review: ['审核工作台', '内容治理 / 修订与删除审核'],
  projects: ['售前工作台', '客户项目 / 资料、框架与方案拼装'],
  kb: ['知识问答', '知识库 / 问答与引用'],
  favorites: ['我的收藏', '个人 / 单页收藏'],
  accounts: ['账号管理', '组织 / 账号、角色、策略'],
  models: ['模型管理', 'AI 能力 / 模型与服务配置'],
}

export function titleFor(pathname: string): [string, string] {
  const key = pathname.split('/')[1] ?? 'library'
  return TITLES[key] ?? ['售前知识库', '售前知识库 / 目录与资产']
}

export default function AppShell({
  roles,
  principalId,
  onChangePassword,
  signingOut,
  onSignOut,
  banner,
  children,
}: {
  roles: string[]
  principalId: string
  onChangePassword: () => void
  signingOut: boolean
  onSignOut: () => void
  banner?: ReactNode
  children: ReactNode
}) {
  const location = useLocation()
  const reviews = useReviewNotifications()
  const [title, crumb] = titleFor(location.pathname)
  const visible = NAV.filter((item) => item.permissions.some(permission => can(roles, permission)))
  const admin = NAV_ADMIN.filter((item) => item.permissions.some(permission => can(roles, permission)))

  const renderItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) => `nav-button${isActive ? ' is-active' : ''}`}
      aria-current={location.pathname.startsWith(item.to) ? 'page' : undefined}
    >
      <Icon name={item.ico} />
      <span className="grow">{item.label}</span>
      {item.key === 'review' ? <PendingCount count={reviews.total} label="审核工作台" /> : item.count ? <span className="count">{item.count}</span> : null}
    </NavLink>
  )

  return (
    <div className="app">
      <a className="skip-link" href="#view">跳到主内容</a>
      <aside className="sidebar">
        <div className="brand">
          售前 AI 知识库
          <small>方案生产工作台 · 售前 AI 知识库</small>
        </div>
        <nav className="nav-group" aria-label="业务导航">
          {visible.map((item) => (
            <Fragment key={item.to}>
              {renderItem(item)}
              {item.key === 'library' ? <LibraryFolderTree roles={roles} /> : null}
            </Fragment>
          ))}
        </nav>
        {admin.length > 0 ? (
          <>
            <div className="nav-sep" />
            <nav className="nav-group" aria-label="管理导航">
              {admin.map(renderItem)}
            </nav>
          </>
        ) : null}
      </aside>

      <div className="main">
        <header className="topbar">
          <div>
            <h1>{title}</h1>
            <div className="crumb">{crumb}</div>
          </div>
          <div className="spacer" />
          {reviews.total > 0 ? <Link className="review-pending-link" to={reviews.revision_pending ? '/review?section=revision' : '/review?section=archive'}><Icon name="review" size={15} /><span>待审核</span><PendingCount count={reviews.total} label="待审核" /></Link> : null}
          <div className="user-actions"><div className="user-info"><div className="avatar">{principalId.slice(0,1).toUpperCase()}</div><b>{principalId}</b></div><button type="button" className="btn" onClick={onChangePassword} disabled={signingOut}>修改密码</button>
          <button type="button" className="btn" onClick={onSignOut} disabled={signingOut} title="退出登录">
            <Icon name="logout" size={14} />
            {signingOut ? '正在退出…' : '退出登录'}
          </button></div>
        </header>
        {banner}
        <main className="content" id="view" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  )
}
